import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { LedgerStatementService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-record-seller-payout-dialog',
  templateUrl: './record-seller-payout.dialog.html',
  standalone: false,
})
export class RecordSellerPayoutDialog {
  private toastSvc = inject(ToastService);
  private ledgerStatementSvc = inject(LedgerStatementService);
  private formBuilder = inject(FormBuilder);

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() warrantyId: string | null = null;
  @Input() contractNumber: string | null = null;

  /** Emitted on every close, carrying whether a payout was actually recorded — the page only
   * needs to refresh the statement when it was. */
  @Output() readonly closed = new EventEmitter<boolean>();

  // 4 decimal places, not the usual 2 — matches the ledger's own precision (LEDGER_AMOUNT_DECIMAL_PRECISION)
  // so a prorated/split payout amount (e.g. $19.2108) can be recorded exactly, not rounded to the cent.
  protected currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    // $0 is a deliberate, valid entry — logs that this contract's commission was netted against
    // an outstanding balance elsewhere rather than actually paid out.
    allowZero: true,
    decimal: '.',
    nullable: true,
    precision: 4,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
    min: 0,
  };

  protected readonly form = this.formBuilder.group({
    amount: [null as number | null, [Validators.required, Validators.min(0)]],
    payoutDate: [new Date().toISOString().split('T')[0], Validators.required],
    paymentMethod: [''],
    description: ['', [Validators.required]],
  });

  protected saving = false;
  private recorded = false;

  open() {
    this.form.reset({
      amount: null,
      payoutDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      description: '',
    });
    this.recorded = false;
    this.saving = false;
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  protected async submit() {
    const { amount, payoutDate, paymentMethod, description } = this.form.value;
    // amount == null, not !amount — $0 is a deliberate, valid entry (netted against an
    // outstanding balance elsewhere), not "nothing entered yet".
    if (!this.warrantyId || amount == null || this.form.invalid || this.saving) return;

    this.saving = true;

    try {
      await this.ledgerStatementSvc.recordPayout(this.warrantyId, {
        amount,
        payoutDate: payoutDate ? new Date(payoutDate) : undefined,
        paymentMethod: paymentMethod || undefined,
        description: description ?? '',
      });

      this.recorded = true;

      this.toastSvc.show({
        type: 'success',
        title: 'Payout recorded',
        text: `A $${amount.toFixed(2)} payout has been recorded for this contract.`,
      });

      this.close();
    } catch (err) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to record payout',
        text:
          err instanceof Error
            ? err.message
            : ((err as { error?: { message?: string } })?.error?.message ?? 'Unknown error'),
      });
    } finally {
      this.saving = false;
    }
  }

  // Bound to the dialog's own close so the X, the backdrop and Escape all refresh the page too.
  protected onClosed() {
    this.closed.emit(this.recorded);
  }
}
